import React, { useEffect, useRef } from "react"
import { Chart, BarElement, BarController, CategoryScale, LinearScale, Tooltip } from "chart.js"
import type { SeriesSummary } from "~/api/portfolio_summaries"
import { formatCurrency, POSITIVE_COLOR, NEGATIVE_COLOR } from "./utils"

Chart.register(BarElement, BarController, CategoryScale, LinearScale, Tooltip)

interface CashFlowChartProps {
  series: SeriesSummary[]
  periodLabel: string
}

const CashFlowChart: React.FC<CashFlowChartProps> = ({
  series,
  periodLabel,
}) => {
  const chartRef = useRef<HTMLCanvasElement>(null)
  const chartInstanceRef = useRef<Chart<"bar"> | null>(null)

  useEffect(() => {
    if (!chartRef.current) return

    const active = series
      .filter((s) => s.called_q > 1000 || s.dist_q > 1000)
      .sort((a, b) => b.dist_q - a.dist_q - (b.called_q - a.called_q))

    if (chartInstanceRef.current) chartInstanceRef.current.destroy()
    const ctx = chartRef.current.getContext("2d")
    if (!ctx) return

    chartInstanceRef.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: active.map((s) => s.name),
        datasets: [
          {
            label: "Distributions",
            data: active.map((s) => s.dist_q),
            backgroundColor: POSITIVE_COLOR,
            borderRadius: 2,
            maxBarThickness: 14,
          },
          {
            label: "Calls",
            data: active.map((s) => -s.called_q),
            backgroundColor: NEGATIVE_COLOR,
            borderRadius: 2,
            maxBarThickness: 14,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (c) => ` ${c.dataset.label}: ${formatCurrency(Math.abs(c.parsed.y))}`,
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            ticks: { autoSkip: true, maxRotation: 90, minRotation: 90, font: { size: 9 } },
            grid: { display: false },
          },
          y: {
            stacked: true,
            grid: { color: "#eef0f2" },
            ticks: { callback: (v) => formatCurrency(v as number) },
          },
        },
      },
    })

    return () => {
      chartInstanceRef.current?.destroy()
      chartInstanceRef.current = null
    }
  }, [series])

  return (
    <div className="b-portfolio-summary-card">
      <div className="b-portfolio-summary-card-header">
        <div>
          <h2>Calls &amp; distributions by series</h2>
          <div className="b-portfolio-summary-card-sub">
            Within {periodLabel}
          </div>
        </div>
        <span className="b-portfolio-summary-hint">
          Calls ▼ · Distributions ▲
        </span>
      </div>
      <div className="b-portfolio-summary-card-body">
        <div className="b-portfolio-summary-chart-wrap b-portfolio-summary-chart-wrap--tall">
          <canvas ref={chartRef} />
        </div>
      </div>
    </div>
  )
}

export default CashFlowChart
